/* eslint-disable no-undef */
import useInitialConfigStore from "../storeModules/useInitialConfigStore";
import useConsorcioStore from "../storeModules/useConsorcioStore";
import useUserStore from "../storeModules/useUserStore";
import useInsuranceStore from "../storeModules/useInsuranceStore";
import {
  CategoriasEnum,
  rendaExtraOptions,
  tipoPessoa,
} from "../utils/constants";
import sha256 from "crypto-js/sha256";
import {
  convertPhoneToHash,
  convertPhoneToGTM,
} from "../utils/convertPhone/index";
import { getRemoteValueEnv } from "../setup";

const isDp6TagsEnabled = () => {
  try {
    return getRemoteValueEnv("is_dp6_tags_dev", "is_dp6_tags_prod").asBoolean();
  } catch (error) {
    return false;
  }
};

const sendTag = (tag) => {
  if (isDp6TagsEnabled()) {
    return;
  }

  const { isApp } = useInitialConfigStore.getState();

  window.dataLayer.push(tag);

  if (
    (typeof AppSendTag === "function" || typeof AppSendTag === "object") &&
    isApp
  ) {
    AppSendTag.postMessage(tag.event);
  }
};

const tagInicio = () => {
  sendTag({
    event: "comecar",
  });
};

const tagGtmStart = () => {
  sendTag({
    "gtm.start": new Date().getTime(),
    event: "gtm.js",
  });
};

const tagSimularOrigem = (brand, siteDomain, origin) => {
  sendTag({
    event: "simular_origem",
    marca: brand,
    site_domain: siteDomain,
    tipo_origem: origin,
  });
};

const tagSimularConsorcio = () => {
  const { categoriaId } = useConsorcioStore.getState();
  const { basic } = useUserStore.getState();
  const { shouldUseCoupon, seguro } = useInitialConfigStore.getState();

  sendTag({
    event: "simular_consorcio",
    produto_categoria: CategoriasEnum[categoriaId].tag,
    tipo_simulacao: "valor_credito",
    valor_escolhido: basic.valorCredito,
    seguro_habilitado: seguro ?? false,
    cupom_habilitado: shouldUseCoupon ?? false,
  });
};

const tagRenda = () => {
  const { basic } = useUserStore.getState();

  sendTag({
    event: "renda_mensal",
    valor_renda: basic.rendaMensal,
  });
};

const tagRendaExtra = (rendaOption) => {
  const { basic } = useUserStore.getState();

  if (rendaExtraOptions.NAO_TENHO === rendaOption) {
    const newCredit = basic.valorCredito;

    sendTag({
      event: "renda_extra",
      tipo_botao_renda: rendaOption,
      valor_renda: basic.rendaMensal,
      valor_renda_extra: null,
      valor_novo_credito: newCredit,
    });
  }

  if (rendaExtraOptions.TENHO === rendaOption) {
    const newCredit = basic.valorCredito;
    const rendaComRendaExtra = basic.rendaMensal + basic.rendaExtra;
    const rendaExtra = basic.rendaExtra;

    sendTag({
      event: "renda_extra",
      tipo_botao: rendaOption,
      valor_renda: rendaComRendaExtra,
      valor_renda_extra: rendaExtra,
      valor_novo_credito: newCredit,
    });
  }

  if (rendaExtraOptions.FALAR_COM_ESPECIALISTA === rendaOption) {
    sendTag({
      event: "renda_extra",
      tipo_botao: rendaOption,
      valor_renda: basic.rendaMensal,
      valor_renda_extra: null,
      valor_novo_credito: null,
    });
  }
};

const tagLeadSave = (
  emailHash,
  telefoneHash,
  email,
  nome,
  telefone,
  leadId,
) => {
  sendTag({
    event: "lead_save",
    email: emailHash,
    telefone: telefoneHash,
    email_cliente: email,
    telefone_cliente: telefone,
    nome: nome,
    lead_id: leadId,
  });
};

const tagPlanosDisponiveis = () => {
  sendTag({
    event: "planos_disponiveis",
  });
};

const tagPlanoSelecionado = () => {
  sendTag({
    event: "plano_selecionado",
  });
};

const tagSemPlanos = (codigo, mensagem) => {
  sendTag({
    event: "plano_indisponivel",
    motivo_codigo: codigo,
    motivo_mensagem: mensagem,
  });
};

const tagIdentificacao = (tipoPessoaNumber, hashCpfCnpj) => {
  sendTag({
    event: "identificacao",
    tipo_cliente: tipoPessoa[tipoPessoaNumber],
    unique_user_id: hashCpfCnpj,
  });
};

const tagDadosContrato = () => {
  const { contrato, basic } = useUserStore.getState();

  const telefoneFormatado = convertPhoneToGTM(basic.telefone);
  const telefoneHash = convertPhoneToHash(basic.telefone);
  const emailHash = sha256(basic.email).toString();

  sendTag({
    event: "dados_contrato",
    nome_razao: contrato.nomeRazao,
    data_nascimento_fundacao: contrato.nascimentoFundacao,
    email: basic.email,
    email_cliente: emailHash,
    telefone: telefoneHash,
    telefone_cliente: telefoneFormatado,
    cep: contrato.cep,
  });
};

const tagAlterarDadosContrato = () => {
  sendTag({
    event: "alterar_dados_contrato",
  });
};

const tagProposta = (propostaId, contrato) => {
  const { userCupom } = useUserStore.getState();
  const { isInsuranceHired, idCotacao, valorPremio } =
    useInsuranceStore.getState();

  sendTag({
    event: "proposta_gerada",
    proposta_id: propostaId,
    contrato_id: contrato,
    aceite_regulamento: true,
    seguro_aceito: isInsuranceHired,
    cupom_utilizado: userCupom ? true : false,
    seguro_perfil: idCotacao ? "Elegível" : "Não Elegível",
    valor_premio: valorPremio,
  });
};

const tagMetodoPagamento = (metodoPagamento) => {
  sendTag({
    event: "metodo_pagamento_selecionado",
    metodo_pagamento: metodoPagamento,
  });
};

const tagMetodoPagamentoAlterado = () => {
  sendTag({
    event: "metodo_pagamento_alterado",
  });
};

const tagStatusPagamento = (statusPagamento) => {
  sendTag({
    event: "status_pagamento",
    metodo_pagamento: "cartao_de_credito",
    status_transacao: statusPagamento,
  });
};

const tagSemMetodoPagamento = () => {
  sendTag({
    event: "sem_metodo_pagamento",
  });
};

const tagPurchase = (paymentType) => {
  const purchaseCategoriaId = useConsorcioStore.getState().categoriaId;
  const purchaseBemCredito =
    useConsorcioStore.getState().selectedPlano.bemCredito;
  const purchaseBemNome = useConsorcioStore.getState().selectedPlano.bemNome;
  const purchasePlanoMeses =
    useConsorcioStore.getState().selectedPlano.planoMeses;
  const purchaseContrato =
    useConsorcioStore.getState().fetchedProposta.contrato;
  const purchaseCidade = useUserStore.getState().contrato.cidade;
  const purchaseBrand = useInitialConfigStore.getState().brand;

  sendTag({
    event: "purchase",
    ecommerce: {
      transaction_id: purchaseContrato,
      affiliation: purchaseBrand,
      value: purchaseBemCredito,
      currency: "BRL",
      payment_type: paymentType,
      items: [
        {
          item_name: purchaseBemNome,
          item_id: purchaseCategoriaId,
          price: purchaseBemCredito,
          item_brand: purchaseBrand,
          plano_meses: purchasePlanoMeses,
          location_id: purchaseCidade,
          index: 1,
          quantity: 1,
        },
      ],
    },
  });
};

const tagPesquisa = (userPesquisa) => {
  sendTag({
    event: "pesquisa",
    ...userPesquisa,
  });
};

const tagBtnWhatsapp = (origin) => {
  sendTag({
    event: "botao_whatsapp",
    etapa: origin,
  });
};

const tagNovaSimulacao = () => {
  sendTag({
    event: "nova_simulacao",
  });
};

const tagDetalhesPlano = () => {
  sendTag({
    event: "detalhes_plano",
  });
};

const tagComprarNovaCota = (
  btn_nova_cota = false,
  mesmo_credito_parcelas = false,
) => {
  sendTag({
    event: "comprar_nova_cota",
    btn_nova_cota: btn_nova_cota,
    mesmo_credito_parcelas: mesmo_credito_parcelas,
  });
};

const tagSemCupom = (stringSemCupom) => {
  sendTag({
    event: stringSemCupom,
  });
};

const tagAplicarCupom = () => {
  sendTag({
    event: "aplicar_cupom",
  });
};

const tagValidarCupom = (isValid, codigoCupom) => {
  sendTag({
    event: "validar_cupom",
    cupom_valido: isValid,
    cupom_codigo: codigoCupom,
  });
};

const tagAplicarNovoCupom = () => {
  sendTag({
    event: "aplicar_novo_cupom",
  });
};

const tagContinuarSemCupom = () => {
  sendTag({
    event: "continuar_sem_cupom",
  });
};

export default sendTag;

export {
  // tagSimularOrigem,
  tagSimularConsorcio,
  tagRenda,
  tagLeadSave,
  tagPlanosDisponiveis,
  tagPlanoSelecionado,
  tagIdentificacao,
  tagDadosContrato,
  tagProposta,
  tagMetodoPagamento,
  tagMetodoPagamentoAlterado,
  tagPurchase,
  tagPesquisa,
  tagSemMetodoPagamento,
  tagStatusPagamento,
  tagBtnWhatsapp,
  tagInicio,
  tagNovaSimulacao,
  tagRendaExtra,
  tagSemPlanos,
  tagAlterarDadosContrato,
  tagDetalhesPlano,
  tagComprarNovaCota,
  tagSemCupom,
  tagAplicarCupom,
  tagValidarCupom,
  tagAplicarNovoCupom,
  tagContinuarSemCupom,
  tagGtmStart,
  tagSimularOrigem,
};
